<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import Select from 'primevue/select'
import D3DonutChart from '@/components/charts/D3DonutChart.vue'
import api from '@/api'
import { useI18n } from '@/i18n'
import { regionNameFromISO2, normalizeISO2 } from '@/utils/country'

const { t, activeLang } = useI18n()

const props = defineProps({
  activeFilters: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['filter'])

const loading = ref(true)
const error   = ref('')
const stats   = ref(null)
const days    = ref(30)

const daysOptions = computed(() => [
  { label: t('hackwatch.posts.statsDays7'),   value: 7 },
  { label: t('hackwatch.posts.statsDays30'),  value: 30 },
  { label: t('hackwatch.posts.statsDays90'),  value: 90 },
  { label: t('hackwatch.posts.statsDays365'), value: 365 },
])

async function fetchStats() {
  loading.value = true
  error.value   = ''
  try {
    const { data: res } = await api.getPostsStats({ days: days.value })
    stats.value = res?.data ?? null
  } catch {
    error.value = t('hackwatch.posts.statsLoadFailed')
  } finally {
    loading.value = false
  }
}

watch(days, () => fetchStats())

const countryLabel = (code) => {
  const c = normalizeISO2(code) || code
  return regionNameFromISO2(c, [activeLang.value]) || c
}

const groupsData = computed(() => (stats.value?.groups ?? []).map(r => ({
  label: r.name,
  value: r.count,
  raw:   r.id,
  name:  r.name,
})))

const countriesData = computed(() => (stats.value?.countries ?? []).map(r => ({
  label: r.name ? countryLabel(r.name) : t('hackwatch.posts.statsUnknown'),
  value: r.count,
  raw:   r.name ?? null,
})))

const industriesData = computed(() => (stats.value?.industries ?? []).map(r => ({
  label: r.name,
  value: r.count,
  raw:   r.name,
})))

function onSliceClick(dimension, item) {
  if (item.isOther || item.raw == null) return
  const payload = { dimension, value: item.raw }
  if (dimension === 'group') payload.label = item.name ?? item.label
  emit('filter', payload)
}

onMounted(fetchStats)
defineExpose({ refresh: fetchStats })
</script>

<template>
  <div class="posts-stats-panel">

    <!-- ── Top Gruppen (mit Zeitfenster) ─────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-users hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.posts.statsGroups') }}</span>
        <Select
          v-model="days"
          :options="daysOptions"
          option-label="label"
          option-value="value"
          size="small"
          class="psp-days-select"
        />
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="groupsData"
          :other-label="t('hackwatch.posts.statsOther')"
          :selected="props.activeFilters?.group ?? null"
          @slice-click="item => onSliceClick('group', item)"
        />
      </div>
    </div>

    <!-- ── Top Länder ─────────────────────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-flag hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.posts.statsCountries') }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="countriesData"
          :other-label="t('hackwatch.posts.statsOther')"
          :selected="props.activeFilters?.country ?? null"
          @slice-click="item => onSliceClick('country', item)"
        />
      </div>
    </div>

    <!-- ── Top Branchen ───────────────────────────────────────────────────── -->
    <div class="hw-card">
      <div class="hw-card-header">
        <i class="pi pi-briefcase hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.posts.statsIndustries') }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="industriesData"
          :other-label="t('hackwatch.posts.statsOther')"
          :selected="props.activeFilters?.industry ?? null"
          @slice-click="item => onSliceClick('industry', item)"
        />
      </div>
    </div>

    <div v-if="!loading && error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
  </div>
</template>

<style scoped>
.posts-stats-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.psp-days-select {
  font-size: .72rem;
}


</style>
